import React, { useEffect, useState } from 'react';

import { Save, Settings, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { filter, find, flow, isEmpty, map, remove } from 'lodash/fp';
import axios from 'views/I9/modules/axios';

const GestionDPAssistant = () => {
  const [open, setOpen] = useState(false);
  const [removeIds, setRemoveIds] = useState([]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    if (isSubmitting) return;

    setOpen(false);
    handleReset();
  };

  const [individuCentraleDP, setIndividuCentraleDP] = useState([]);
  const [individuCentraleAssists, setIndividuCentraleAssists] = useState([]);
  const [listFullIndiv, setListFullIndiv] = useState([]);

  const getCMAPIndividu = async () => {
    const res = await axios.get(`individu_centrale?_associations=*individu&individu.delete=0`);

    setIndividuCentraleDP(filter('individu_centrale_isDp', res.data));
    setIndividuCentraleAssists(filter('individu_centrale_isAssist', res.data));
  };

  const getListDp = async () => {
    const res = await axios.get(`individu?_sort=nom,prenom&delete=0&idpartenaire=3`);

    setListFullIndiv(res.data);
  };

  useEffect(() => {
    getCMAPIndividu();
    getListDp();
  }, []);

  const { isSubmitting, values, handleSubmit, setFieldValue, handleReset } = useFormik({
    initialValues: {
      dp: { list: individuCentraleDP, selected: '' },
      assists: { list: individuCentraleAssists, selected: '' },
    },
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        // Add new ones
        await Promise.all(
          flow(
            filter({ individu_centrale_id: null }),
            map(({ individu, ...individu_centrale }) => {
              return axios.post(`individu_centrale`, individu_centrale);
            }),
          )([...values.dp.list, ...values.assists.list]),
        );

        // Delete those that have been removed (if any)
        if (!isEmpty(removeIds)) {
          axios.delete(`individu_centrale?${map(id => `ids[]=${id}`, removeIds).join('&')}`);
        }
      } catch (error) {
        console.error('Impossible de mettre à jour la liste des dp / assists', error);
      }

      await getCMAPIndividu();
    },
  });

  const removeIndividu = name => (id, idindividu) => {
    setFieldValue(`${name}.list`, remove({ individu_centrale_idindividu: idindividu }, values[name].list));

    if (id) {
      setRemoveIds([...removeIds, id]);
    }
  };

  if (!individuCentraleDP || !individuCentraleAssists) {
    return null;
  }

  return (
    <>
      <Button variant='contained' color='primary' onClick={handleOpen}>
        <Settings />
      </Button>
      <Modal open={open} onClose={handleClose}>
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              minWidth: 700,
              maxHeight: 900,
              overflowY: 'auto',
            }}
          >
            <Grid container justifyContent='flex-end'>
              <Box mb={1} mr={1}>
                <Button disabled={isSubmitting} type='submit' variant='contained' color='primary'>
                  <Save />
                </Button>
              </Box>
            </Grid>
            <Paper>
              <Box padding={2}>
                <Box mb={2}>
                  <Typography variant='h5'>
                    <strong>Gestion délégué(e)s programmes et assistant(e)s</strong>
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {/* List DP */}
                  <Grid item xs={6}>
                    <Typography variant='h6' paragraph>
                      Délégué(e)s programmes
                    </Typography>
                    <LocalTable
                      allIndividus={listFullIndiv}
                      name='dp'
                      removeIndividu={removeIndividu}
                      setFieldValue={setFieldValue}
                      values={values.dp}
                    />
                  </Grid>
                  {/* List Assists */}
                  <Grid item xs={6}>
                    <Typography variant='h6' paragraph>
                      Assistant(e)s
                    </Typography>
                    <LocalTable
                      allIndividus={listFullIndiv}
                      name='assists'
                      removeIndividu={removeIndividu}
                      setFieldValue={setFieldValue}
                      values={values.assists}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        </form>
      </Modal>
    </>
  );
};

const LocalTable = ({ allIndividus, values, name, setFieldValue, removeIndividu }) => {
  return (
    <TableContainer>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>{name === 'dp' ? 'Délégué(e)' : 'Assistant(e)'}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {values.list.map(row => (
            <TableRow key={`${row.individu_centrale_id}-${row.individu_centrale_idindividu}`}>
              <TableCell>
                {row.individu?.nom} {row.individu?.prenom}
              </TableCell>
              <TableCell align='right'>
                <IconButton
                  onClick={() => removeIndividu(name)(row.individu_centrale_id, row.individu_centrale_idindividu)}
                  size='large'
                >
                  <Delete color='primary' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          <TableRow key='add-new-dp'>
            <TableCell colSpan={2}>
              <FormControl fullWidth variant='outlined' size='small'>
                <InputLabel>Ajouter un(e) {name === 'dp' ? 'délégué(e)' : 'assistant(e)'}</InputLabel>
                <Select
                  label={`Ajouter un(e) ${name === 'dp' ? 'délégué(e)' : 'assistant(e)'}`}
                  value={values.selected}
                  name={`${name}.selected`}
                  onChange={event => {
                    setFieldValue(`${name}.list`, [
                      ...values.list,
                      {
                        individu: find({ idindividu: event.target.value }, allIndividus),
                        individu_centrale_id: null,
                        individu_centrale_idindividu: event.target.value,
                        individu_centrale_isDp: name === 'dp' ? 1 : null,
                        individu_centrale_isAssist: name === 'assists' ? 1 : null,
                      },
                    ]);

                    setFieldValue(`${name}.selected`, '');
                  }}
                >
                  {/* Remove those already selected */}
                  {filter(
                    ({ idindividu }) => !find({ individu_centrale_idindividu: idindividu }, values.list),
                    allIndividus,
                  ).map(individu => (
                    <MenuItem
                      key={individu.idindividu}
                      value={individu.idindividu}
                    >{`${individu.nom} ${individu.prenom}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GestionDPAssistant;
